import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { tokenStore } from "@/lib/token";
import { ArrowRight, Check, Sparkles, ShoppingBag, Users, Zap, Eye, EyeOff, ChevronLeft, Shield, Rocket } from "lucide-react";

function getPasswordStrength(password) {
  let score = 0;
  if (password.length >= 8) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return score; // 0-5
}

const STRENGTH_LABELS = ["Très faible", "Faible", "Moyen", "Bon", "Fort", "Excellent"];
const STRENGTH_COLORS = ["bg-red-500", "bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-green-500", "bg-emerald-500"];

export default function Onboarding() {
  const { user, login, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [userName, setUserName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [needsUsername, setNeedsUsername] = useState(false);

  // A Google/Discord sign-up for an account that doesn't exist yet carries no
  // session at all: nothing was saved server-side until the password below is
  // validated (see /auth/complete-signup). This is the token that will create it.
  // Oublié dès que le compte est créé : un retour au mot de passe passe alors par
  // /auth/set-password (le jeton d'inscription ne sert qu'une fois).
  const [pendingSignup, setPendingSignup] = useState(() => {
    try {
      const raw = sessionStorage.getItem("pending_signup");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  // Étape « nom d'utilisateur » (numéro interne 6) : affichée en 2e position quand elle est demandée.
  const [askedUsername, setAskedUsername] = useState(false);
  useEffect(() => { if (step === 6) setAskedUsername(true); }, [step]);
  const stepOrder = askedUsername ? [1, 6, 2, 3, 4, 5] : [1, 2, 3, 4, 5];
  const stepPosition = Math.max(1, stepOrder.indexOf(step) + 1);
  const stepPercent = Math.round((stepPosition / stepOrder.length) * 100);

  const strength = useMemo(() => getPasswordStrength(password), [password]);
  // Un mot de passe fort est conseillé (voir la jauge ci-dessous) mais non obligatoire.
  const canSubmitPassword = password.length > 0;

  useEffect(() => {
    if (pendingSignup) return;
    // Arrivée après une connexion Google/Discord : le compte se charge encore.
    // Sans cette attente, la page renvoyait à l'accueil avant de l'avoir lu.
    if (authLoading) return;
    if (!user) {
      navigate("/");
      return;
    }
    setUserName(user.username || user.email?.split("@")[0] || "");
    setNeedsUsername(Boolean(user.needs_username));
  }, [user, navigate, pendingSignup, authLoading]);

  const handlePasswordSubmit = async () => {
    if (!canSubmitPassword) {
      toast.error("Veuillez entrer un mot de passe");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    setLoading(true);
    try {
      if (pendingSignup) {
        // Ce n'est qu'ici que le compte est réellement créé.
        const response = await axios.post(`${API}/auth/complete-signup`, {
          pending_token: pendingSignup.pending_token,
          password,
          referred_by_code: localStorage.getItem("vantapulse_ref_code") || undefined,
        });
        sessionStorage.removeItem("pending_signup");
        localStorage.removeItem("vantapulse_ref_code");
        setPendingSignup(null);
        login(response.data.access_token, response.data.user);
        toast.success("Compte créé avec succès");
        setStep(response.data.user.needs_username ? 6 : 2);
        return;
      }

      const token = tokenStore.get();
      if (!token) {
        toast.error("Session expirée. Veuillez vous reconnecter.");
        navigate("/", { replace: true });
        return;
      }
      await axios.post(`${API}/auth/set-password`, {
        password,
        auth_header: `Bearer ${token}`
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Mot de passe défini avec succès");
      setStep(needsUsername ? 6 : 2);
    } catch (error) {
      console.error("Set password error:", error);
      toast.error(error.response?.data?.detail || (pendingSignup ? "Session d'inscription expirée, recommencez la connexion" : "Erreur lors de la définition du mot de passe"));
      if (pendingSignup) {
        sessionStorage.removeItem("pending_signup");
        navigate("/", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUsernameSubmit = async () => {
    const username = userName.trim();
    if (username.length < 2 || username.length > 32) {
      toast.error("Le nom d'utilisateur doit contenir entre 2 et 32 caractères");
      return;
    }
    setLoading(true);
    try {
      const token = tokenStore.get();
      await axios.post(`${API}/auth/set-username`, { username, auth_header: `Bearer ${token}` });
      setNeedsUsername(false);
      setStep(2);
      toast.success("Nom d'utilisateur enregistré");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement du nom");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOnboarding = () => {
    if (pendingSignup) {
      sessionStorage.removeItem("pending_signup");
    }
    navigate("/", { replace: true });
  };

  const goToShop = () => {
    navigate("/shop");
  };

  const renderStep = () => {
    switch (step) {
      case 6:
        return (
          <div className="space-y-8 animate-slideUp">
            <div className="text-center"><Users className="w-14 h-14 mx-auto mb-5 text-blue-400" /><h2 className="text-3xl font-bold text-white mb-3">Choisissez votre nom d'utilisateur</h2><p className="text-white/50 text-lg">Ce nom sera affiché sur votre compte VantaPulse.</p></div>
            <div><label className="block text-sm text-white/60 mb-2 font-medium">Nom d'utilisateur</label><input value={userName} onChange={(e) => setUserName(e.target.value)} autoFocus placeholder="Votre nom" className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-blue-500/50" /></div>
            <button onClick={handleUsernameSubmit} disabled={loading} className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold flex items-center justify-center gap-3 disabled:opacity-50">{loading ? "Enregistrement..." : "Continuer"}<ArrowRight className="w-5 h-5" /></button>
          </div>
        );
      case 1:
        return (
          <div className="space-y-8 animate-slideUp">
            <div className="text-center relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 blur-3xl rounded-full"></div>
              <div className="relative">
                <Shield className="w-16 h-16 mx-auto mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 animate-pulse" />
                <h2 className="text-3xl font-bold text-white mb-3 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Sécurisez votre compte
                </h2>
                <p className="text-white/50 text-lg">Créez votre mot de passe VantaPulse pour un accès sécurisé</p>
              </div>
            </div>
            
            <div className="space-y-5">
              <div className="group">
                <label className="block text-sm text-white/60 mb-2 font-medium">Mot de passe</label>
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg blur opacity-20 group-hover:opacity-40 transition-opacity"></div>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="relative w-full px-5 py-4 pr-12 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all backdrop-blur-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {password.length > 0 && (
                  <div className="mt-2">
                    <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden flex gap-1">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className={`h-full flex-1 rounded-full transition-colors ${i < strength ? STRENGTH_COLORS[strength] : "bg-transparent"}`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-white/40 mt-1">{STRENGTH_LABELS[strength]} — conseil : min. 8 caractères, majuscule, minuscule et chiffre</p>
                  </div>
                )}
              </div>
              <div className="group">
                <label className="block text-sm text-white/60 mb-2 font-medium">Confirmer le mot de passe</label>
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg blur opacity-20 group-hover:opacity-40 transition-opacity"></div>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="relative w-full px-5 py-4 pr-12 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all backdrop-blur-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>

            <button
              onClick={handlePasswordSubmit}
              disabled={loading || !canSubmitPassword || password !== confirmPassword}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-xl font-semibold transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3 disabled:opacity-50 disabled:transform-none shadow-lg shadow-blue-500/25"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Création en cours...
                </>
              ) : (
                <>
                  Continuer
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        );

      case 2:
        return (
          <div className="text-center space-y-8 animate-slideUp">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-green-500/30 to-emerald-500/30 blur-3xl rounded-full animate-pulse"></div>
              <div className="relative w-24 h-24 mx-auto bg-gradient-to-br from-green-500 to-emerald-600 rounded-3xl flex items-center justify-center shadow-2xl shadow-green-500/30 transform rotate-12 hover:rotate-0 transition-transform duration-500">
                <Check className="w-12 h-12 text-white" />
              </div>
            </div>
            <div className="space-y-4">
              <h2 className="text-4xl font-bold text-white">
                Bienvenue, <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">{userName}</span> !
              </h2>
              <p className="text-white/50 text-lg">Votre compte VantaPulse est maintenant prêt à l'emploi</p>
            </div>
            <button
              onClick={() => setStep(3)}
              className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white rounded-xl font-semibold transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3 shadow-lg shadow-green-500/25"
            >
              <Rocket className="w-5 h-5" />
              Découvrir VantaPulse
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        );

      case 3:
        return (
          <div className="space-y-8 animate-slideUp">
            <div className="text-center relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 blur-3xl rounded-full"></div>
              <div className="relative">
                <ShoppingBag className="w-16 h-16 mx-auto mb-6 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400" />
                <h2 className="text-3xl font-bold text-white mb-3 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  Notre boutique exclusive
                </h2>
                <p className="text-white/50 text-lg">Des produits premium pour votre serveur</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-xl blur-xl group-hover:blur-2xl transition-all"></div>
                <div className="relative bg-white/5 border border-white/10 rounded-2xl p-6 text-center backdrop-blur-sm hover:bg-white/10 transition-all transform hover:-translate-y-1">
                  <Zap className="w-10 h-10 mx-auto mb-3 text-yellow-400" />
                  <p className="text-sm text-white/80 font-medium">Ultra Rapide</p>
                </div>
              </div>
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl blur-xl group-hover:blur-2xl transition-all"></div>
                <div className="relative bg-white/5 border border-white/10 rounded-2xl p-6 text-center backdrop-blur-sm hover:bg-white/10 transition-all transform hover:-translate-y-1">
                  <Users className="w-10 h-10 mx-auto mb-3 text-green-400" />
                  <p className="text-sm text-white/80 font-medium">Communauté</p>
                </div>
              </div>
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl blur-xl group-hover:blur-2xl transition-all"></div>
                <div className="relative bg-white/5 border border-white/10 rounded-2xl p-6 text-center backdrop-blur-sm hover:bg-white/10 transition-all transform hover:-translate-y-1">
                  <Sparkles className="w-10 h-10 mx-auto mb-3 text-blue-400" />
                  <p className="text-sm text-white/80 font-medium">Premium</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setStep(5)}
              className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-xl font-semibold transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3 shadow-lg shadow-purple-500/25"
            >
              Continuer
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        );

      case 5:
        return (
          <div className="text-center space-y-8 animate-slideUp">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-cyan-500/30 blur-3xl rounded-full animate-pulse"></div>
              <div className="relative w-24 h-24 mx-auto bg-gradient-to-br from-blue-500 to-cyan-500 rounded-3xl flex items-center justify-center shadow-2xl shadow-blue-500/30 transform hover:scale-110 transition-transform duration-300">
                <ShoppingBag className="w-12 h-12 text-white" />
              </div>
            </div>
            <div className="space-y-4">
              <h2 className="text-4xl font-bold text-white">
                Vous êtes <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">prêt</span> !
              </h2>
              <p className="text-white/50 text-lg">Commencez à explorer notre boutique</p>
            </div>
            <button
              onClick={goToShop}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-xl font-semibold transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3 shadow-lg shadow-blue-500/25"
            >
              <ShoppingBag className="w-5 h-5" />
              Accéder à la boutique
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-pink-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="bg-[#12121a]/80 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
          {/* Progress bar */}
          <div className="mb-8">
            <div className="flex justify-between mb-2">
              <span className="text-xs text-white/40 font-medium">Étape {stepPosition} sur {stepOrder.length}</span>
              <span className="text-xs text-white/40 font-medium">{stepPercent}%</span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all duration-700 ease-out"
                style={{ width: `${stepPercent}%` }}
              />
            </div>
          </div>

          {/* Step content with animation */}
          <div className="min-h-[350px] flex items-center justify-center">
            <div 
              key={step}
              className="w-full animate-slideUp"
            >
              {renderStep()}
            </div>
          </div>

          {/* Back button — en flux normal pour ne jamais chevaucher le bouton principal.
              Étape 1 seulement : annule l'onboarding. Une fois le mot de passe créé, il ne se
              remplace plus ici (Paramètres ou « mot de passe oublié ») : pas de retour vers lui. */}
          {step === 1 && (
            <div className="text-center mt-4">
              <button
                onClick={handleCancelOnboarding}
                className="text-sm text-white/40 hover:text-white/60 transition-colors inline-flex items-center gap-1.5"
              >
                <ChevronLeft className="w-4 h-4" />
                Retour
              </button>
            </div>
          )}
        </div>

        <style>{`
          @keyframes slideUp {
            from { 
              opacity: 0; 
              transform: translateY(30px) scale(0.95);
            }
            to { 
              opacity: 1; 
              transform: translateY(0) scale(1);
            }
          }
          .animate-slideUp {
            animation: slideUp 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          }
        `}</style>
      </div>
    </div>
  );
}
